import { flatMap, maxBy, range, values } from 'lodash'
import isNull from 'lodash/isNull'

import { calcSqnLevel, currencyFormatter } from '../../utils'

import { ChartOptions } from './types'

export const CHART_COLORS = {
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
}

export function getChartColors(dataLength: number): string[] {
  const colorGroups = values(CHART_COLORS)
  const maxColorsPerGroup = maxBy(colorGroups, 'length')?.length || 0
  const groupCount = colorGroups.length

  return flatMap(range(dataLength), (i) => {
    const groupIndex = i % groupCount
    const colorIndex = Math.floor(i / groupCount) % maxColorsPerGroup
    return colorGroups[groupIndex][colorIndex]
  })
}

export const chartOptions = (options = {} as ChartOptions) => {
  const {
    currency,
    showPercentage,
    customTooltipLabelData,
    isSqn,
    displayLegend = false,
    disableLegendClick = true,
  } = options
  return {
    plugins: {
      legend: {
        display: displayLegend,
        position: 'top' as const,
        labels: {
          boxWidth: 8,
          boxHeight: 8,
        },
        ...(disableLegendClick && { onClick: () => null }),
      },
      tooltip: {
        callbacks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: function (context: any) {
            let label = context.dataset.label || ''

            if (label) {
              label += ': '
            }

            if (customTooltipLabelData && label && customTooltipLabelData.data.length) {
              if (customTooltipLabelData.title) {
                return (
                  customTooltipLabelData.title +
                  ': ' +
                  customTooltipLabelData.data[context.dataIndex].amount
                )
              }

              return label + customTooltipLabelData.data[context.dataIndex].amount
            }

            if (context.parsed.y !== null) {
              if (showPercentage) {
                label += context.parsed.y + '%'
              } else {
                label += currency
                  ? currencyFormatter(currency, context.parsed.y, {}, false)
                  : context.parsed.y
              }
            }
            return label
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          afterLabel: (context: any) => {
            if (customTooltipLabelData) {
              const { data } = customTooltipLabelData

              if (isSqn && data.length) {
                const { value } = data[context.dataIndex]
                const sqnLevel = calcSqnLevel(value)
                return `SQN: ${isNull(value) ? '-' : Number(value).toFixed(2)} \n${sqnLevel}`
              }
            }

            return ''
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          font: {
            family: 'Wix Madefor Text',
            weight: 'bold' as const,
          },
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          // stepSize: currency ? 1000 : 5,
          padding: 24,
          callback: function (value: string | number) {
            if (currency) {
              return currencyFormatter(currency, Number(value), {}, true)
            } else if (showPercentage) {
              return `${value}%`
            } else {
              return value
            }
          },
          font: {
            family: 'Wix Madefor Text',
            weight: 'bold' as const,
          },
        },
        border: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          dash: (context: any) => {
            if (context.tick?.value === 0) {
              return []
            } else {
              return [4, 4]
            }
          },
          display: false,
        },
      },
    },
  }
}
